import gql from "graphql-tag";
import type { DocumentNode } from "graphql";

export const PRESENTATION_ANALYTICS_PAGE_SIZE = 25;

export type PresentationAnalyticsSelectorMode = "top" | "workspace" | "search";

export interface PresentationAnalyticsSelectorResult {
  position: number;
  documentId: string;
  workspaceId: string;
  workspacePath: string[];
  qualifiedViews: string | number;
  hasMore: boolean;
  document?: {
    id: string;
    displayTitle: string;
    thumbnailDownloadUrl?: string | null;
  } | null;
}

interface PresentationAnalyticsSelectorConnection {
  nodes: Array<PresentationAnalyticsSelectorResult | null>;
}

export interface PresentationAnalyticsTopDocumentsQuery {
  presentationAnalyticsTopDocuments?: PresentationAnalyticsSelectorConnection | null;
  presentationAnalyticsHasEligibleDocuments?: boolean | null;
}

export interface PresentationAnalyticsDocumentsByWorkspaceQuery {
  presentationAnalyticsDocumentsByWorkspace?: PresentationAnalyticsSelectorConnection | null;
}

export interface SearchPresentationAnalyticsDocumentsQuery {
  searchPresentationAnalyticsDocuments?: PresentationAnalyticsSelectorConnection | null;
}

const selectorResultFragment = gql`
  fragment PresentationAnalyticsSelectorResultFields on PresentationAnalyticsDocumentResult {
    position
    documentId
    workspaceId
    workspacePath
    qualifiedViews
    hasMore
    document {
      id
      displayTitle
      thumbnailDownloadUrl
    }
  }
`;

export const presentationAnalyticsTopDocumentsDocument = gql`
  query PresentationAnalyticsTopDocuments {
    presentationAnalyticsHasEligibleDocuments
    presentationAnalyticsTopDocuments {
      nodes {
        ...PresentationAnalyticsSelectorResultFields
      }
    }
  }
  ${selectorResultFragment}
`;

export const presentationAnalyticsDocumentsByWorkspaceDocument = gql`
  query PresentationAnalyticsDocumentsByWorkspace(
    $selectedWorkspaceId: BigInt!
    $pageSize: Int!
    $pageOffset: Int!
  ) {
    presentationAnalyticsDocumentsByWorkspace(
      selectedWorkspaceId: $selectedWorkspaceId
      pageSize: $pageSize
      pageOffset: $pageOffset
    ) {
      nodes {
        ...PresentationAnalyticsSelectorResultFields
      }
    }
  }
  ${selectorResultFragment}
`;

export const searchPresentationAnalyticsDocumentsDocument = gql`
  query SearchPresentationAnalyticsDocuments(
    $searchTerm: String!
    $selectedWorkspaceId: BigInt
    $pageSize: Int!
    $pageOffset: Int!
  ) {
    searchPresentationAnalyticsDocuments(
      searchTerm: $searchTerm
      selectedWorkspaceId: $selectedWorkspaceId
      pageSize: $pageSize
      pageOffset: $pageOffset
    ) {
      nodes {
        ...PresentationAnalyticsSelectorResultFields
      }
    }
  }
  ${selectorResultFragment}
`;

export function getPresentationAnalyticsSelectorMode({
  searchTerm,
  workspaceId,
}: {
  searchTerm: string;
  workspaceId?: string;
}): PresentationAnalyticsSelectorMode {
  if (searchTerm.trim().length > 0) {
    return "search";
  }

  if (workspaceId) {
    return "workspace";
  }

  return "top";
}

export function readSelectorQueryValue(
  value: string | (string | null)[] | null | undefined,
): string {
  if (Array.isArray(value)) {
    return value[0] ?? "";
  }

  return value ?? "";
}

export function selectorQueryForMode({
  mode,
  searchTerm,
  workspaceId,
  offset,
}: {
  mode: PresentationAnalyticsSelectorMode;
  searchTerm: string;
  workspaceId?: string;
  offset: number;
}): {
  document: DocumentNode;
  variables: Record<string, unknown>;
  responseKey:
    | "presentationAnalyticsTopDocuments"
    | "presentationAnalyticsDocumentsByWorkspace"
    | "searchPresentationAnalyticsDocuments";
} {
  if (mode === "search") {
    return {
      document: searchPresentationAnalyticsDocumentsDocument,
      variables: {
        searchTerm: searchTerm.trim(),
        selectedWorkspaceId: workspaceId || null,
        pageSize: PRESENTATION_ANALYTICS_PAGE_SIZE,
        pageOffset: offset,
      },
      responseKey: "searchPresentationAnalyticsDocuments",
    };
  }

  if (mode === "workspace") {
    return {
      document: presentationAnalyticsDocumentsByWorkspaceDocument,
      variables: {
        selectedWorkspaceId: workspaceId,
        pageSize: PRESENTATION_ANALYTICS_PAGE_SIZE,
        pageOffset: offset,
      },
      responseKey: "presentationAnalyticsDocumentsByWorkspace",
    };
  }

  return {
    document: presentationAnalyticsTopDocumentsDocument,
    variables: {},
    responseKey: "presentationAnalyticsTopDocuments",
  };
}
